import type { ExistingDesk } from "@/lib/desk-suggestions/existing-desks";
import type {
  ProvisionDeskRequest,
  SuggestedDesk,
  SuggestedSubdesk,
} from "@/lib/types/desk-suggestions";

/** service-desk's limits for provisioned desks and subdesks. */
export const DESK_DESCRIPTION_MIN_LENGTH = 10;
export const DESK_DESCRIPTION_MAX_LENGTH = 1000;
export const DESK_NAME_MAX_LENGTH = 60;
export const SUBDESK_NAME_MAX_LENGTH = 60;
export const SUBDESK_DESCRIPTION_MAX_LENGTH = 1000;

export interface SubdeskSelection {
  name: string;
  description: string;
}

/** Why service-desk would reject this desk, or null when it is valid. */
export function deskValidationError(
  desk: SuggestedDesk,
  description: string,
): string | null {
  if (desk.name.trim().length > DESK_NAME_MAX_LENGTH) {
    return `The desk name is longer than ${DESK_NAME_MAX_LENGTH} characters.`;
  }
  const length = description.trim().length;
  if (length > DESK_DESCRIPTION_MAX_LENGTH) {
    return `The description is longer than ${DESK_DESCRIPTION_MAX_LENGTH} characters.`;
  }
  if (length === 0 && desk.template_id !== null) {
    return null;
  }
  if (length < DESK_DESCRIPTION_MIN_LENGTH) {
    return desk.template_id === null
      ? `Custom desks need a description of at least ${DESK_DESCRIPTION_MIN_LENGTH} characters.`
      : `Descriptions need at least ${DESK_DESCRIPTION_MIN_LENGTH} characters, or leave it empty to keep the default desk's description.`;
  }
  return null;
}

/** Why service-desk would reject this subdesk, or null when it is valid. */
export function subdeskValidationError(
  subdesk: SuggestedSubdesk,
  description: string,
): string | null {
  // service-desk derives the subdesk id from the name's letters and digits.
  if (!/[\p{L}\p{N}_]/u.test(subdesk.name)) {
    return "The subdesk name needs at least one letter or digit.";
  }
  if (subdesk.name.trim().length > SUBDESK_NAME_MAX_LENGTH) {
    return `The subdesk name is longer than ${SUBDESK_NAME_MAX_LENGTH} characters.`;
  }
  if (description.trim().length > SUBDESK_DESCRIPTION_MAX_LENGTH) {
    return `The description is longer than ${SUBDESK_DESCRIPTION_MAX_LENGTH} characters.`;
  }
  return null;
}

function toSubdeskRequests(subdesks: SubdeskSelection[]) {
  return subdesks.map((subdesk) => ({
    name: subdesk.name.trim(),
    description: subdesk.description.trim() || null,
  }));
}

/** Creates the suggested desk with the selected subdesks. */
export function newDeskRequest(
  desk: SuggestedDesk,
  description: string,
  subdesks: SubdeskSelection[],
): ProvisionDeskRequest {
  return {
    name: desk.name,
    description: description.trim() || null,
    emoji: desk.emoji,
    template_id: desk.template_id,
    subdesks: toSubdeskRequests(subdesks),
  };
}

function isValidDeskDescription(value: string | null | undefined): boolean {
  const length = value?.trim().length ?? 0;
  return (
    length >= DESK_DESCRIPTION_MIN_LENGTH &&
    length <= DESK_DESCRIPTION_MAX_LENGTH
  );
}

/**
 * Adds the selected subdesks to an existing desk. The request names the
 * existing desk (its template id, else its name) so service-desk's provisioning
 * matches it and only creates the missing subdesks. The description only
 * satisfies the request's validation: an existing desk's is never changed.
 */
export function syncDeskRequest(
  desk: SuggestedDesk,
  existing: ExistingDesk,
  subdesks: SubdeskSelection[],
): ProvisionDeskRequest {
  const templateId = existing.template_id ?? null;
  let description: string | null = null;
  if (templateId === null) {
    description =
      [existing.description, desk.description]
        .find(isValidDeskDescription)
        ?.trim() ?? `Subdesks of the ${existing.name} desk`;
  }
  return {
    name: existing.name,
    description,
    emoji: null,
    template_id: templateId,
    subdesks: toSubdeskRequests(subdesks),
  };
}

export interface SelectionCounts {
  newDesks: number;
  newDeskSubdesks: number;
  existingDesks: number;
  existingDeskSubdesks: number;
}

export function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

/**
 * What submitting the selection does, e.g.
 * "Create 2 desks · add 3 subdesks to 1 existing desk". Empty when nothing is
 * selected.
 */
export function selectionSummary(counts: SelectionCounts): string {
  const parts: string[] = [];
  if (counts.newDesks > 0) {
    let part = `create ${plural(counts.newDesks, "desk")}`;
    if (counts.newDeskSubdesks > 0) {
      part += ` with ${plural(counts.newDeskSubdesks, "subdesk")}`;
    }
    parts.push(part);
  }
  if (counts.existingDesks > 0) {
    parts.push(
      `add ${plural(counts.existingDeskSubdesks, "subdesk")} to ` +
        plural(counts.existingDesks, "existing desk"),
    );
  }
  const summary = parts.join(" · ");
  return summary.charAt(0).toUpperCase() + summary.slice(1);
}
